import moment, { Moment } from 'moment';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RRule } from 'rrule';

import { VisibilityOptions } from '~src/modules/okrs/constants/options';
import SelectorHandler from '~src/modules/okrs/manageObjectives/shared/SelectorHandler';
import RepeatSelector from '~src/modules/okrs/manageObjectives/shared/repetition/RepeatSelector';
import { InlineSubInfoContainer, Repetition, Visibility } from '~src/modules/okrs/manageObjectives/shared/shared.sc';
import VisibilitySelector, { VisibilityObj } from '~src/modules/okrs/manageObjectives/shared/visibility/VisibilitySelector';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import SvgIcon from '~src/shared/SvgIcon';
import CustomDatePicker from '~src/shared/form/inputs/CustomDatePicker';
import FrozenInput from '~src/shared/form/inputs/FrozenInput';
import { DoubleDates } from '~src/shared/form/shared.sc';
import { ObjectiveItem, ObjectiveItemUpdate, ObjectiveStatus } from '~src/store/okrs/interfaces';
import { checkDateFormat } from '~src/utils';
import {useDateFormatter} from "~src/services/hooks/useDateFormatter";

interface OwnProps {
  hasChangePermissions: boolean;
  objective: ObjectiveItem;
  update: (values: Partial<ObjectiveItemUpdate>) => ApiReduxResp<ObjectiveItem>;
}

type Props = OwnProps;

const SubInfoEdit: FC<Props> = (props) => {
  const [startDate, setStartDate] = useState<string | null>(props.objective.start_date);
  const [dueDate, setDueDate] = useState<string | null>(props.objective.due_date);
  const [dateError, setDueDateError] = useState<string | null>(null);

  const changeStartDate = async (date: Moment | null) => {
    const parsedDate = date ? moment.utc(date).format() : null;
    setStartDate(parsedDate);
    const compareDate = dueDate ? moment.utc(dueDate) : null;
    if (date !== null && compareDate !== null && date.isAfter(compareDate)) {
      setDueDateError('okr.due_date_before_start');
      return;
    }
    setDueDateError(null);
    await props.update({ start_date: parsedDate || undefined });
  };

  const changeDueDate = async (date: Moment | null) => {
    const parsedDate = date ? moment.utc(date).format() : null;
    const compareDate = startDate ? moment.utc(startDate) : null;
    setDueDate(parsedDate);
    if (date !== null && compareDate !== null && date.isBefore(compareDate)) {
      setDueDateError('okr.due_date_before_start');
      return;
    }
    setDueDateError(null);
    await props.update({ due_date: parsedDate || undefined });
  };

  const [reminderVal, setReminderVal] = useState<string | undefined>(
    new RRule({
      ...props.objective.reminder_options,
      dtstart: null,
      until: null,
    }).toString(),
  );

  const changeReminder = async (reminder: string) => {
    await props.update({ reminder_options: RRule.fromString(reminder).options });
    setReminderVal(reminder);
  };

  const clearReminder = async () => {
    await props.update({ reminder_options: undefined });
    setReminderVal(undefined);
  };

  const [visibilityVal, setVisibilityVal] = useState<VisibilityObj>({
    key: props.objective.visibility,
    shareWithManagers: props.objective.vis_owners_managers,
    people: props.objective.visibilities,
  });

  const changeVisibility = async (visibilityObj: VisibilityObj) => {
    if (visibilityObj.key !== VisibilityOptions.custom || !!visibilityObj.people || visibilityObj.shareWithManagers) {
      await props.update({
        visibility: visibilityObj.key,
        vis_owners_managers: visibilityObj.shareWithManagers,
        visibilities: visibilityObj.people ? visibilityObj.people.map((vis) => vis.id) : undefined,
      });
      setVisibilityVal(visibilityObj);
    }
  };

  const [t] = useTranslation();

  const dateFormatter = useDateFormatter();
  const currentDueDate = checkDateFormat(dueDate);
  const currentStartDate = checkDateFormat(startDate);
  const isEnabled = props.hasChangePermissions && props.objective.status !== ObjectiveStatus.CLOSED;
  return (
    <InlineSubInfoContainer>
      <SelectorHandler hoverTextKey="x.objective.add.icon.dates" iconName="iconCalenderTime">
        {() => (
          <DoubleDates>
            {isEnabled ? (
              <CustomDatePicker
                error={dateError}
                allowClear={false}
                dateValue={currentStartDate}
                label={t('x.okr.objective.form.dates.start')}
                labelKey="x.okr.objective.form.dates.start"
                onChange={changeStartDate}
              />
            ) : (
              <FrozenInput
                value={
                  currentStartDate && typeof currentStartDate !== 'string'
                    ? dateFormatter(currentStartDate)
                    : currentDueDate
                }
                label={t('x.okr.objective.form.dates.start')}
                labelKey="x.okr.objective.form.dates.start"
              />
            )}
            {isEnabled ? (
              <CustomDatePicker
                error={dateError}
                allowClear={false}
                dateValue={currentDueDate}
                label={t('x.okr.objective.form.dates.due')}
                labelKey="x.okr.objective.form.dates.due"
                onChange={changeDueDate}
              />
            ) : (
              <FrozenInput
                value={
                  currentDueDate && typeof currentDueDate !== 'string'
                    ? dateFormatter(currentDueDate)
                    : currentDueDate
                }
                label={t('x.okr.objective.form.dates.due')}
                labelKey="x.okr.objective.form.dates.due"
              />
            )}
          </DoubleDates>
        )}
      </SelectorHandler>
      {(props.objective.key_result_owners && props.objective.key_result_owners.length && (
        <SelectorHandler hoverTextKey="x.objective.add.icon.reminder" iconName="iconAlarmClock">
          {() => (
            <>
              {isEnabled ? (
                /* key_result_owners as proxy for key results */
                <Repetition>
                  <RepeatSelector
                    onChange={changeReminder}
                    value={reminderVal}
                    error={undefined}
                    label={t('x.okr.objective.form.reminder')}
                  />
                  {reminderVal && (
                    <div onClick={clearReminder}>
                      <SvgIcon width={20} name="iconCross" />
                    </div>
                  )}
                </Repetition>
              ) : (
                <FrozenInput
                  value={reminderVal ? RRule.fromString(reminderVal).toText() : ''}
                  label={t('x.okr.objective.form.reminder')}
                  labelKey="x.okr.objective.form.reminder"
                />
              )}
            </>
          )}
        </SelectorHandler>
      )) ||
        ''}
      <SelectorHandler hoverTextKey="x.objective.add.icon.visibility" iconName="iconEye">
        {() =>
          isEnabled ? (
            <VisibilitySelector
              value={visibilityVal}
              onChange={changeVisibility}
              label={t('x.okr.objective.form.visibility')}
              error={null}
            />
          ) : (
            <Visibility>
              <FrozenInput
                value={visibilityVal.key}
                label={t('x.okr.objective.form.visibility')}
                labelKey="REPLACE"
              />
            </Visibility>
          )
        }
      </SelectorHandler>
    </InlineSubInfoContainer>
  );
};

export default SubInfoEdit;
